import { TERMS_HTML, PRIVACY_HTML } from './legalContentFormatted';

export interface LegalContent {
  title: string;
  content: string;
  lastUpdated: string;
  formattedHTML?: string; // Pre-formatted HTML for instant display
}

export const LEGAL_CONTENT: Record<'privacy' | 'terms' | 'disclaimer', LegalContent> = {
  privacy: {
    title: 'Privacy Policy',
    content: `Last Updated: December 16, 2025

Wlkthru, LLC ("Wlkthru," "we," "our," or "us") is committed to maintaining the accuracy, confidentiality, and security of personally identifiable information ("Information" or "Personal Information"). As part of this commitment, our Privacy Policy governs our actions as they relate to the collection, use, and disclosure of Personal Information. This Privacy Policy applies to users in the United States. Wlkthru may expand to other regions in the future, at which time this policy will be updated accordingly. Wlkthru's Terms of Service are incorporated herein by reference.

Identifying Purposes: Data We Process and Why

We collect, use, or disclose Personal Information to provide our customers with a superior product and service experience. Wlkthru's technologies are intended to benefit our customers and the trades industry. We use all information we collect to help improve our products, support our services, and advance technology in the trades industry.

Our purposes include but are not limited to:
• To provide, improve, or develop products and services
• To provide shortcuts or suggestions to customers such as tagging, time, date, and location information
• To test features in development, analyze information to evaluate and improve products and services, develop new products or features, and conduct audits or troubleshooting activities
• To communicate with customers about our products and services
• To send marketing communications and let customers know about our policies and terms
• To respond to customer inquiries and support requests
• To promote safety and security, verify activity, and investigate suspicious activity or violations of our Terms of Service
• To train artificial intelligence and machine learning models for robotics and automation technology
• To advance research and development in the trades industry

Video Call Recording and Consent

Recording Disclosure

ALL VIDEO CALLS CONDUCTED THROUGH WLKTHRU MAY BE RECORDED. By joining a video call through our platform, you consent to being recorded.

Service providers using Wlkthru must inform their customers that calls may be recorded BEFORE the call begins. Service providers are solely responsible for complying with all applicable federal, state, and local laws regarding recording of phone calls and video calls, including obtaining all necessary consents from participants.

Important State Law Notice: Recording laws vary significantly by state. Some states require all parties to consent to recording, including California, Connecticut, Florida, Illinois, Maryland, Massachusetts, Michigan, Montana, Nevada, New Hampshire, Pennsylvania, and Washington. Service providers must ensure they comply with the laws applicable to both their location and their customer's location.

Ownership and Use of Recorded Content

Wlkthru retains ownership of all video recordings created through the platform. We store and retain video recordings indefinitely for the following purposes:
• Service delivery and quality assurance
• Product improvement and development
• Training artificial intelligence and machine learning models
• Developing robotics and automation technology for the trades industry
• Research and development purposes

Anonymization and De-identification

When video data is used for AI/ML training or shared with third parties, it will be anonymized through the following methods:
• Blurring or removing faces and other biometric identifiers
• Removing or distorting audio that contains personally identifiable information
• Removing metadata that could identify specific individuals or locations

Data Sharing for Technology Development

By using Wlkthru's services, you acknowledge and agree that your anonymized video data may be used for AI/ML training and shared with third parties as described in this policy. This use is automatic and helps improve technology for the trades industry.

Anonymized video data may be shared with or licensed to:
• Technology companies developing AI and machine learning systems
• Robotics companies building automation solutions for trades industries
• Research institutions studying trades industry workflows
• Other third parties for legitimate technology development purposes

Wlkthru may receive compensation for licensing this anonymized data.

Opt-Out Rights

If you do not wish to have your anonymized video data used for AI/ML training or shared with third parties for technology development purposes, you may opt out by contacting us at support@wlkthru.co with your request. Please include your account information or phone number associated with your video calls.

Important Notes:
• Opting out does not prevent recording, which is necessary for service delivery and quality assurance
• Opt-out requests may take up to 30 days to process
• Opting out will only apply to future recordings. Video data that has already been anonymized and used for training purposes prior to your opt-out request cannot be removed from existing datasets

Biometric Data

Video recordings may contain biometric information such as facial geometry and voice patterns. By using our service, you acknowledge that such biometric data may be collected and processed as described in this policy. When video data is shared with third parties for AI/ML training, biometric identifiers will be anonymized or removed in accordance with applicable state biometric privacy laws.

Types of Information Wlkthru Collects

The type of information Wlkthru collects depends on the product or service you use. This includes but is not limited to:

General Information
• First name
• Last name
• Phone number (to deliver meeting links via SMS text message)
• Email address
• Company name
• Video call recordings (audio and visual)
• Snapshots taken during calls or from recording playback
• Call metadata (duration, time, date, participants)
• Device ID to differentiate users in the system

Information You Provide

Wlkthru collects content and information you provide when using our services, including when signing up for an account, creating or sharing content, messaging, or communicating with others. This may include information in or about the content you provide, such as the location of a photo or the date a file was created. We also collect information about how you use our services, such as the types of content viewed or engaged with and the frequency and duration of your activities.

Information Others Provide

Wlkthru collects content and information that other people provide when they use our services or a Wlkthru customer's services, including information about you. This includes when audio/visual information is shared, when messages or communications are sent to you, or when information is uploaded, synced, imported, or otherwise accessed.

Networks and Connections

Wlkthru collects information about the people or groups you are connected to and how you interact with them, such as the people you communicate with most or the types of groups you service or share information with. We may collect contact information you provide if you upload, sync, or import this information (such as an address book) from a device.

Payment Information

If you use our services for purchases or financial transactions, we collect limited information necessary to process your payment, such as transaction amount, billing details, and payment confirmation.

Wlkthru does not collect, store, or have access to complete credit or debit card information. All payments are processed securely through trusted third-party payment processors who are certified to meet industry standards (including PCI-DSS) for handling payment data. We receive only the information necessary to confirm a successful transaction (such as payment amount, date, and limited card details like the last four digits).

Device Information

Wlkthru collects information from or about computers, phones, or other devices where you install or access our services, depending on permissions you grant or as otherwise permissible by law. We may associate information collected from your different devices. This includes:
• Operating system, hardware version, device settings, file and software names and types, battery and signal strength, and device identifiers
• Device locations, including specific geographic locations through GPS, Bluetooth, or WiFi signals
• Connection information such as mobile operator or ISP name, browser type, language and time zone, mobile phone number, and IP address

Information from Websites and Apps

Wlkthru collects information when you visit or use third-party websites and apps that use or access our products or services, including information about the websites and apps you use or visit, your use of our products or services on those websites and apps, and information the developer or publisher provides to you or us.

Consent

By accessing and using Wlkthru, you grant consent to use your Personal Information as described in this Privacy Policy. We respect that consent and your privacy.

How Information Is Shared

Information you provide may be shared in the following situations:

Sharing Through Our Services

People use our services to connect and share with others. When you use our services, information may be shared:
• With people you share and communicate with
• As public information if you share with a public audience
• When others download or re-share your content
• When you or others comment on posts or content
• When your audience is set to public

Service Providers and End Customers

We share end-customer data (name, phone number, email) with the service provider who initiated the video call. This is necessary to provide the core functionality of our platform.

Third-Party Apps, Websites, and Integrations

When you use third-party apps, websites, or services that are integrated with Wlkthru's services, those third parties may receive information about what is recorded, posted, or shared. Information collected by these third parties is subject to their own terms and policies.

Technology and Research Partners

We share anonymized and de-identified video data with technology companies, robotics developers, and research institutions for AI/ML training and technology development purposes as described in the Video Call Recording section above.

Service Providers and Vendors

We share information with third-party service providers who help us provide or improve our products and services, including:
• Video hosting and processing providers
• Cloud storage providers
• Analytics services (using non-personally identifiable information only)
• Marketing and advertising partners
• Third-party payment processors
• Customer support tools

Wlkthru requires all service providers to handle your information securely and in accordance with applicable data protection laws.

Business Transfers

If ownership or control of all or part of Wlkthru or our assets changes, we may transfer your information to the new owner. We will notify you via email or through a prominent notice on our website before your information is transferred.

Legal Compliance

We may disclose information to comply with applicable law, regulation, legal process, or governmental request, including to meet national security requirements.

Marketing and Communications

SMS and Email Marketing

By providing your phone number to Wlkthru or through a service provider using Wlkthru, you consent to receive SMS text messages from Wlkthru, including marketing messages about our services, updates, and promotions. Message and data rates may apply.

You can opt out at any time by:
• Replying STOP to any SMS message
• Clicking the unsubscribe link in any marketing email
• Contacting support@wlkthru.co

Opting out of marketing communications does not affect service-related communications, such as account notifications, security alerts, or transactional messages.

CAN-SPAM and TCPA Compliance

All marketing emails will contain an unsubscribe link. We comply with the CAN-SPAM Act and the Telephone Consumer Protection Act (TCPA) for all communications.

Data Retention

We retain your personal information for as long as necessary to fulfill the purposes stated in this Privacy Policy:
• Video recordings: Retained indefinitely for service improvement, quality assurance, and AI/ML training purposes
• Personal contact information: Retained as long as your account is active plus seven (7) years for business records and compliance purposes
• Anonymized data: Retained indefinitely for research, development, and training purposes
• Payment information: Wlkthru does not retain full credit or debit card data. All payment credentials are securely handled by third-party payment processors in accordance with financial record-keeping laws and industry standards.

You may request deletion of your personal information by contacting support@wlkthru.co. However, anonymized data that has already been used for AI/ML training may be retained.

Security Safeguards

Wlkthru takes data security very seriously. We have implemented appropriate administrative, technical, and physical safeguards to prevent unauthorized access, use, modification, disclosure, or destruction of the information you entrust to us, including:
• Encryption in transit and at rest for all video recordings
• Access controls and authentication requirements
• Regular security audits and monitoring
• Secure cloud storage with redundancy
• Employee training on data protection

All financial transactions are processed through secure third-party payment processors that comply with applicable data security and encryption standards.

If you have concerns regarding the security of your data, please contact us at support@wlkthru.co.

Your Rights and Choices

Access to Information

You may request access to the personal information we hold about you by contacting support@wlkthru.co.

Correction and Updates

You have the right to update or correct inaccuracies in your personal information. You can update your account information directly or contact us for assistance.

Deletion Requests

You may request deletion of your personal information. We will honor such requests subject to legal obligations and legitimate business needs. Note that anonymized data already used in AI/ML training datasets cannot be removed.

Marketing Opt-Out

You can opt out of marketing communications as described in the Marketing and Communications section above.

AI/ML Data Opt-Out

You can opt out of having your video data used for AI/ML training and third-party sharing as described in the Video Call Recording section above.

Cookies

A cookie is a small computer file or piece of information that may be stored in your computer's hard drive when you visit our websites. We use cookies to improve our website's functionality and, in some cases, to provide visitors with a customized online experience.

Cookies are widely used and most web browsers are configured to accept cookies automatically. If you disable cookies that Wlkthru deems critical, your experience or optimal performance may be compromised.

Children's Privacy

Our products and services are not directed to individuals under 16 years of age. Wlkthru does not knowingly collect personal information from children under 16. If we become aware that a child under 16 has provided us with personal information, we will take steps to delete such information. If you believe that a child under 16 has provided personal information to us, please contact support@wlkthru.co.

External Links

Some pages of our websites may contain links to third-party websites. If you submit personal information to any of these third-party sites, your information is governed by their privacy policies. We recommend that you review the privacy policies of any third-party sites before sharing personal information.

Important Legal Disclaimers

Recording Consent Responsibility

Service providers using Wlkthru are solely responsible for complying with all applicable federal, state, and local laws regarding recording of phone calls and video calls. This includes obtaining all necessary consents from participants before recording begins. Wlkthru provides the technology platform but does not provide legal advice regarding recording consent requirements, which vary by jurisdiction. Service providers should consult with legal counsel regarding their specific obligations.

Service providers using our platform indemnify and hold harmless Wlkthru from any claims, damages, or liability arising from their failure to obtain proper recording consent or comply with applicable recording laws.

Legal Consultation Recommendation

This privacy policy is designed for general compliance purposes. Wlkthru recommends that users consult with their own legal counsel regarding their specific recording consent obligations, biometric data requirements, and privacy requirements in their jurisdiction.

Limitation of Liability

To the fullest extent permitted by law, Wlkthru shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services.

Changes to This Privacy Policy

We may modify this Privacy Policy at any time. We will notify you of significant changes through a service announcement or by sending an email to your primary email address. If we make significant changes that affect your rights, you will be provided with at least 30 days' advance notice.

Your continued use of our services after the effective date of changes to the Privacy Policy will be deemed to be your agreement to the modified Privacy Policy.

Contact Us

For users in the United States, please contact us at:
Email: support@wlkthru.co
Website: www.wlkthru.co

Acceptance of Terms

By using Wlkthru's services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy and our Terms of Service.`,
    lastUpdated: 'December 16, 2025',
    formattedHTML: PRIVACY_HTML
  },

  terms: {
    title: 'Terms of Service',
    content: `Last Updated: December 16, 2025

These Terms of Service ("Terms") govern your use of Wlkthru's website, products, and mobile applications ("Services"). Please read them carefully before using our platform.

By creating an account, subscribing, or otherwise using Wlkthru, you agree to these Terms and our Privacy Policy (incorporated herein by reference) ("Privacy Policy"). If you do not agree, you may not use our Services.

1. Company Information

Wlkthru, LLC ("Wlkthru," "we," "our," or "us") is a Utah limited liability company providing a video-call platform that allows businesses to virtually connect with their customers before visiting job sites. Examples include home service companies using Wlkthru to diagnose issues, provide bids, and gather information remotely.

Our Services are sold directly to businesses in the United States on a subscription basis and include both web-based access and mobile applications.

2. Eligibility

You must be at least 18 years old and authorized to enter into binding agreements on behalf of your business to use Wlkthru. By using the platform, you represent that you meet these requirements.

3. Account Registration

You must create an account to access certain features. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. Notify us immediately at support@wlkthru.co if you suspect unauthorized use.

4. Subscriptions and Payments

Wlkthru operates on a subscription model. When you subscribe, you agree to pay all applicable fees according to the plan you select.

All payments are processed securely through third-party payment processors. Wlkthru does not collect or store complete credit or debit card information.

Payments are final and non-refundable once completed. Subscription renewals occur automatically unless canceled prior to the renewal date.

5. Use of Services

Wlkthru enables businesses to:
• Conduct virtual video calls with customers,
• Record or download video sessions, and
• Request recorded videos from customers.

You agree to use the platform only for legitimate business purposes and in compliance with all applicable laws.

You are solely responsible for obtaining all necessary recording consents as required under federal, state, and local laws.

6. User Responsibilities

You agree not to:
• Violate any law or regulation, including recording consent laws,
• Upload or share unlawful, harmful, or misleading content,
• Attempt to disrupt, hack, or damage the platform, or
• Resell or misrepresent Wlkthru's Services.

You are responsible for ensuring that your use of the platform complies with all applicable laws, including privacy and communication regulations.

7. Video Recording and Ownership

All video calls conducted through Wlkthru may be recorded. By participating in or hosting a call, you consent to recording.

You hereby assign to Wlkthru all rights, title, and interest in and to any video recordings you make using the Service, together with all copyrights and other intellectual property rights.

Service providers may download copies of their recordings for personal or business reference, and Wlkthru hereby grants a non-exclusive, perpetual license to use such videos for personal or business purposes. Notwithstanding any of the foregoing, you hereby acknowledge that Wlkthru retains all rights, title, and interest in the recordings and any derivatives and related data.

By using Wlkthru, you grant us the right to store, process, and use recordings as outlined in our Privacy Policy, including for quality assurance and technology development purposes.

8. Privacy Policy

Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information. By using our Services, you agree to the collection and use of your information in accordance with our Privacy Policy.

9. Intellectual Property

All intellectual property rights in Wlkthru's platform, software, design, trademarks, and materials are owned by Wlkthru, LLC.

You may not copy, reproduce, distribute, or create derivative works from our materials or other intellectual property except as permitted under these Terms.

All rights not expressly granted are reserved by Wlkthru.

10. Termination

We may suspend or terminate your account at any time, without notice, if we believe you have violated these Terms, misused the platform, or engaged in unlawful activity.

You may stop using Wlkthru at any time by canceling your subscription or contacting support@wlkthru.co.

11. Beta Features

From time to time, Wlkthru may offer access to new or experimental features ("Beta Features"). These are provided for testing and feedback purposes only and may change or be discontinued at any time. If you provide to us any feedback on the Beta Features (collectively, the "Feedback"), you agree to assign and do hereby assign to us all rights, title, and interest in and to the Feedback, together with all intellectual property rights. If Wlkthru incorporates any of your Feedback into the Beta Features or Services, you acknowledge that you will not be entitled to any remuneration for the use of your Feedback.

Beta Features are provided "as is" and may contain bugs, errors, or other issues. Wlkthru is not liable for any damages or losses resulting from your use of Beta Features.

12. Disclaimers

Wlkthru provides its Services on an "as is" and "as available" basis, without warranties of any kind, express or implied.

Although we strive to provide quality Services, we do not guarantee that the Services will be uninterrupted, error-free, or meet your expectations.

WLKTHRU EXPRESSLY DISCLAIMS ALL WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

13. Limitation of Liability

TO THE FULLEST EXTENT PERMITTED BY LAW, WLKTHRU SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, DATA, OR GOODWILL ARISING FROM YOUR USE OF THE SERVICES.

OUR TOTAL LIABILITY FOR ANY CLAIM UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID FOR WLKTHRU IN THE 12 MONTHS PRECEDING THE CLAIM.

14. Indemnification

You agree to indemnify, defend, and hold harmless Wlkthru, its members, managers, officers, employees, and affiliates from any claims, damages, liabilities, and expenses (including reasonable legal fees) arising from your misuse of the Services or violation of these Terms, including failure to comply with recording consent laws.

15. Dispute Resolution and Arbitration

Any dispute, claim, or controversy arising out of or relating to these Terms or the use of Wlkthru's Services shall be resolved by binding arbitration under the Federal Arbitration Act.

Arbitration will be conducted in Utah County, Utah, by a single arbitrator, in accordance with the rules of the American Arbitration Association (AAA).

You and Wlkthru waive the right to a trial by jury or to participate in class actions. Each party shall bear its own arbitration costs unless otherwise required by law.

16. Governing Law

These Terms and any disputes shall be governed by and construed under the laws of the State of Utah, without regard to its conflict of law principles.

17. Changes to Terms

We may modify these Terms at any time. Updates will be posted on our website with the "Last Updated" date revised. Continued use of Wlkthru after such changes constitutes your acceptance of the revised Terms.

18. Contact Us

For questions or legal notices, contact:
Wlkthru, LLC
Email: support@wlkthru.co
Website: www.wlkthru.co

19. Entire Agreement

These Terms, along with our Privacy Policy, constitute the entire agreement between you and Wlkthru, superseding any prior agreements or understandings.

By using Wlkthru, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.`,
    lastUpdated: 'December 16, 2025',
    formattedHTML: TERMS_HTML
  },

  disclaimer: {
    title: 'Disclaimer',
    content: `The information provided by wlkthru ("we," "us," or "our") on our platform is for general informational purposes only. All information on the platform is provided in good faith, however we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information on the platform.

Under no circumstances shall we have any liability to you for any loss or damage of any kind incurred as a result of the use of the platform or reliance on any information provided on the platform. Your use of the platform and your reliance on any information on the platform is solely at your own risk.

User Conduct and Content

wlkthru does not monitor or control the conduct of users during video calls. Users are solely responsible for:
• Their interactions during calls
• Any content shared, recorded, or transmitted
• Obtaining necessary consents for recording
• Compliance with applicable laws and regulations
• The accuracy of measurements taken using AR tools

Third-Party Services

We integrate with third-party services including but not limited to:
• Twilio for video infrastructure
• LiveKit for real-time communication
• Stripe for payment processing
• Supabase for data storage

Your use of these services is subject to their respective terms of service and privacy policies. We are not responsible for the availability, accuracy, or reliability of these third-party services.

Technical Limitations

While we strive to provide reliable service:
• Service interruptions may occur
• Call quality depends on internet connectivity
• AR measurements are approximations and should be verified
• Recording quality may vary based on device and network conditions
• Storage limits apply based on subscription tier

No Professional Advice

The platform does not provide professional advice of any kind. Any information or tools provided should not be considered as:
• Legal advice
• Financial advice
• Technical consulting
• Professional measurements or assessments

Medical and Safety Considerations

Users should be aware that:
• Extended video call usage may cause eye strain
• Proper ergonomics should be maintained
• Users with photosensitive conditions should use caution
• The platform should not be used while driving or operating machinery

Data Backup

While we implement security measures and data backup procedures:
• Users are responsible for maintaining their own backups of important data
• We recommend downloading important recordings before expiration
• We are not liable for data loss due to technical issues or user error

Availability

We do not guarantee uninterrupted availability of our services. Maintenance, updates, or technical issues may cause temporary service disruptions.

Changes to This Disclaimer

We reserve the right to update this disclaimer at any time. Changes will be effective immediately upon posting.

Contact

If you have questions about this disclaimer, please contact us at support@wlkthru.co.

Last updated: December 16, 2025`,
    lastUpdated: 'December 16, 2025'
  }
};
